'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SessionMode } from '@/hooks/useVoiceWidget';
import VoiceRingCTA from './VoiceRingCTA';
import VoiceRingFallback from './VoiceRingFallback';
import './floating-voice-widget.css';

export type FloatingVoiceSurface =
  | 'homepage'
  | 'global_floating'
  | 'simple_setup'
  | 'ai_business_page_marketing'
  | 'tenant_business_page'
  | 'tenant_web_widget'
  | 'enterprise_pricing'
  | 'custom_pricing'
  | 'demo_page'
  | 'request_demo'
  | 'dashboard_inbound_prompt'
  | 'support'
  // Shared TalkToBriModal trigger surfaces (homepage V5) — one literal per
  // CTA that can open the modal, so demo_completed/demo_cta_clicked carry
  // real per-CTA attribution instead of a generic "modal" bucket.
  | 'navbar'
  | 'hero_fab'
  | 'roi'
  | 'final_cta'
  | 'orb'
  | 'sticky_bar'
  // The hero's scripted phone-demo post-call card (Task B3) — distinct from
  // 'hero_fab' (the hero FAB, Task B4) so demo_cta_clicked attribution can
  // tell "opened from the FAB" apart from "opened after watching the demo".
  | 'hero_demo'
  // The hero phone itself, tapped in live mode (2026-07-31) — the phone opens a
  // real Bri session directly instead of playing the scripted transcript. Kept
  // distinct from 'hero_demo' so we can compare the live phone's pull against
  // the scripted card it replaced (which handed off 2 of 30 sessions).
  | 'hero_phone';

export type FloatingVoiceVariant =
  | 'floating'
  | 'centered-ring'
  | 'modal-ring'
  | 'embedded-business-page';

export type FloatingVoiceLabels = Partial<{
  idle: string;
  connecting: string;
  listening: string;
  speaking: string;
  end: string;
  ended: string;
  error: string;
}>;

export type FloatingVoiceHelperText = Partial<Record<'idle' | 'connecting' | 'listening' | 'speaking' | 'ended' | 'error', string>>;

export type FloatingVoicePostCall =
  | { mode: 'none' | 'restart' }
  | {
      mode: 'cta';
      title?: string;
      /** Overrides the default post-call body copy. */
      body?: string;
      href: string;
      label: string;
      dismissLabel?: string;
    };

export interface FloatingVoiceProps {
  surface: FloatingVoiceSurface;
  variant?: FloatingVoiceVariant;
  agentId?: string;
  fallbackAgentId?: string;
  tenantId?: string;
  tenantSlug?: string;
  /** Host origin embedding the widget (embed iframe only); forwarded to the
   *  signed-url endpoint for observability. */
  signedUrlOrigin?: string;
  dynamicVariables?: Record<string, string | number | boolean>;
  overrides?: Record<string, unknown>;
  labels?: FloatingVoiceLabels;
  helperText?: FloatingVoiceHelperText;
  postCall?: FloatingVoicePostCall;
  size?: number;
  className?: string;
  ringClassName?: string;
  testId?: string;
  reveal?: 'immediate' | 'after-scroll';
  autoStart?: boolean;
  autoStartToken?: number;
  visualReveal?: 'deferred' | 'immediate';
  onSessionStart?: () => void;
  /** Fired whenever the underlying widget's session mode changes (idle →
   *  connecting → listening/speaking → ended), once the interactive widget
   *  has loaded. Lets shell consumers (e.g. TalkToBriModal) react to the
   *  post-call state without re-deriving it themselves. */
  onSessionModeChange?: (
    mode: SessionMode,
    context: { conversationId?: string; durationSeconds?: number },
  ) => void;
}

export type FloatingVoiceV1Surface = FloatingVoiceSurface;
export type FloatingVoiceV1Variant = FloatingVoiceVariant;
export type FloatingVoiceV1Props = FloatingVoiceProps;

const loadFloatingVoiceV1Interactive = () => import('./FloatingVoiceV1Interactive');

const FloatingVoiceV1Interactive = dynamic(loadFloatingVoiceV1Interactive, {
  ssr: false,
});

const SHELL_LABELS = {
  idle: 'Tap to Talk',
  connecting: 'Connecting',
} satisfies FloatingVoiceLabels;

function track(event: string, extra?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  try {
    const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event, voice_component: 'floating_voice_v1_shell', ...(extra ?? {}) });
  } catch {
    /* ignore */
  }
}

export default function FloatingVoiceV1({
  surface,
  variant = 'centered-ring',
  agentId,
  labels,
  size,
  className = '',
  ringClassName = '',
  testId = 'floating-voice-v1',
  reveal = variant === 'floating' ? 'after-scroll' : 'immediate',
  autoStart = false,
  autoStartToken = 0,
  visualReveal = 'deferred',
  ...props
}: FloatingVoiceProps) {
  const [isVisible, setIsVisible] = useState(reveal === 'immediate');
  const [interactive, setInteractive] = useState(autoStart || autoStartToken > 0);
  const [renderVisualRing, setRenderVisualRing] = useState(visualReveal === 'immediate');
  const [queuedAutoStartToken, setQueuedAutoStartToken] = useState(autoStart ? 1 : autoStartToken);
  const lastAutoStartTokenRef = useRef(autoStartToken);
  const resolvedSize = size ?? (variant === 'floating' ? 100 : variant === 'modal-ring' ? 200 : 220);
  const shellLabels = { ...SHELL_LABELS, ...(labels ?? {}) };
  const isFloating = variant === 'floating';

  const activateInteractive = useCallback((reason: 'click' | 'intent' | 'auto') => {
    track('floating_voice_shell_upgrade', { voice_surface: surface, voice_variant: variant, reason });
    setInteractive(true);
  }, [surface, variant]);

  const startInteractive = useCallback(() => {
    setRenderVisualRing(true);
    setQueuedAutoStartToken((value) => value + 1);
    activateInteractive('click');
  }, [activateInteractive]);

  useEffect(() => {
    if (reveal === 'immediate') return;

    const onScroll = () => setIsVisible(window.scrollY > window.innerHeight * 0.5);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [reveal]);

  useEffect(() => {
    if (autoStartToken > 0 && autoStartToken !== lastAutoStartTokenRef.current) {
      lastAutoStartTokenRef.current = autoStartToken;
      setQueuedAutoStartToken(autoStartToken);
      activateInteractive('auto');
    }
  }, [activateInteractive, autoStartToken]);

  const preloadInteractive = useCallback(() => {
    if (interactive) return;
    setRenderVisualRing(true);
    track('floating_voice_shell_prefetch', { voice_surface: surface, voice_variant: variant });
    void loadFloatingVoiceV1Interactive();
  }, [interactive, surface, variant]);

  useEffect(() => {
    if (visualReveal === 'immediate' || !isVisible || interactive || renderVisualRing) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let idleId: number | undefined;
    let removeLoadListener: (() => void) | undefined;

    const revealVisualRing = () => {
      setRenderVisualRing(true);
    };

    const requestIdleReveal = () => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(revealVisualRing, { timeout: 4500 });
        return;
      }
      timeoutId = globalThis.setTimeout(revealVisualRing, 4500);
    };

    const scheduleAfterLoad = () => {
      timeoutId = globalThis.setTimeout(requestIdleReveal, variant === 'floating' ? 900 : 4200);
    };

    if (document.readyState === 'complete') {
      scheduleAfterLoad();
    } else {
      window.addEventListener('load', scheduleAfterLoad, { once: true });
      removeLoadListener = () => window.removeEventListener('load', scheduleAfterLoad);
    }

    return () => {
      removeLoadListener?.();
      if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
      if (idleId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [interactive, isVisible, renderVisualRing, variant, visualReveal]);

  if (interactive) {
    return (
      <FloatingVoiceV1Interactive
        {...props}
        surface={surface}
        variant={variant}
        agentId={agentId}
        labels={labels}
        size={size}
        className={className}
        ringClassName={ringClassName}
        testId={testId}
        autoStart={autoStart}
        autoStartToken={queuedAutoStartToken}
        isVisible={isVisible}
      />
    );
  }

  return (
    <div
      data-testid={testId}
      data-floating-voice-v1
      data-voice-surface={surface}
      data-voice-variant={variant}
      data-voice-engine="deferred"
      className={
        isFloating
          ? `fv-root ${isVisible ? 'fv-visible' : ''} ${className}`
          : `fv-inline-root ${className}`
      }
      aria-live="polite"
      onPointerEnter={preloadInteractive}
      onFocusCapture={preloadInteractive}
      onTouchStart={preloadInteractive}
    >
      {variant !== 'floating' && shellLabels.idle && (
        <p className="fv-helper fv-helper--idle">
          {shellLabels.idle}
        </p>
      )}
      {renderVisualRing ? (
        <VoiceRingCTA
          size={resolvedSize}
          onClick={startInteractive}
          className={`${isFloating ? 'fv-btn' : 'transition-transform duration-300 hover:scale-105'} ${ringClassName}`}
          sessionMode="idle"
        />
      ) : (
        <VoiceRingFallback
          size={resolvedSize}
          onClick={startInteractive}
          className={`${isFloating ? 'fv-btn' : 'transition-transform duration-300 hover:scale-105'} ${ringClassName}`}
          sessionMode="idle"
        />
      )}
      {isFloating && (
        <span className="fv-label" aria-hidden="true">
          {shellLabels.idle}
        </span>
      )}
    </div>
  );
}
